import { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Input,
  Select,
  Empty,
  Pagination,
  Slider,
  Tag,
  Button,
  Drawer,
  Space,
  Badge,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { ProductCard, ProductCardSkeleton } from '@digiwedge/ui-axiz';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { searchProducts, type Product } from '../services/products.service';
import { useFilterOptions } from '../hooks/useProductFilters';
import './ShopPage.css';

const { Search } = Input;

const PAGE_SIZE = 24;

interface ActiveFilters {
  searchText: string;
  brands: string[];
  category?: string;
  priceRange: [number, number];
  inStockOnly: boolean;
  sortBy: string;
}

const DEFAULT_FILTERS: ActiveFilters = {
  searchText: '',
  brands: [],
  category: undefined,
  priceRange: [0, 100000],
  inStockOnly: false,
  sortBy: 'relevance',
};

// Responsive column breakpoints for consistent card sizing
const GRID_COLUMNS = {
  xs: 12,  // 2 columns
  sm: 8,   // 3 columns
  md: 6,   // 4 columns
  lg: 6,   // 4 columns
  xl: 4,   // 6 columns
  xxl: 4,  // 6 columns
};

export function ShopPage() {
  const [filters, setFilters] = useState<ActiveFilters>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const filterOptions = useFilterOptions();
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.brands.length > 0) count++;
    if (filters.category) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 100000) count++;
    if (filters.inStockOnly) count++;
    return count;
  }, [filters]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['products', filters, currentPage],
    queryFn: () =>
      searchProducts(
        {
          searchText: filters.searchText || '',
          category: filters.category,
          brand: filters.brands[0],
          priceMin: filters.priceRange[0],
          priceMax: filters.priceRange[1],
          availability: filters.inStockOnly,
          sortOptions: filters.sortBy,
        },
        currentPage - 1,
        PAGE_SIZE,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const products: Product[] = data?.products ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleBrandToggle = (brandId: string) => {
    setFilters((prev) => ({
      ...prev,
      brands: prev.brands.includes(brandId)
        ? prev.brands.filter((b) => b !== brandId)
        : [...prev.brands, brandId],
    }));
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: string | undefined) => {
    setFilters((prev) => ({ ...prev, category: categoryId }));
    setCurrentPage(1);
  };

  const handlePriceRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({ ...prev, priceRange: value }));
  };

  const handleSortChange = (sortBy: string) => {
    setFilters((prev) => ({ ...prev, sortBy }));
    setCurrentPage(1);
  };

  // Skeleton grid for loading state
  const renderSkeletonGrid = () => (
    <Row gutter={[16, 16]}>
      {Array.from({ length: PAGE_SIZE }).map((_, index) => (
        <Col key={index} {...GRID_COLUMNS}>
          <ProductCardSkeleton />
        </Col>
      ))}
    </Row>
  );

  // Empty state component
  const renderEmptyState = () => {
    const hasFilters = activeFilterCount > 0 || filters.searchText;

    return (
      <Empty
        image={<ShoppingOutlined style={{ fontSize: 64, color: 'var(--text-muted)' }} />}
        description={
          <div className="empty-state-content">
            <h3>{hasFilters ? 'No products match your filters' : 'No products available'}</h3>
            <p>
              {hasFilters
                ? 'Try adjusting your search or filter criteria.'
                : 'Check back soon for new products.'}
            </p>
          </div>
        }
      >
        {hasFilters && (
          <Button type="primary" onClick={handleClearFilters}>
            Clear All Filters
          </Button>
        )}
      </Empty>
    );
  };

  const FilterContent = (
    <div className="filter-content">
      <div className="filter-section">
        <h4>Brands</h4>
        <div className="brand-tags">
          {filterOptions.brands.map((brand) => (
            <Tag.CheckableTag
              key={brand.id}
              checked={filters.brands.includes(brand.id)}
              onChange={() => handleBrandToggle(brand.id)}
            >
              {brand.name}
            </Tag.CheckableTag>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <h4>Category</h4>
        <Select
          placeholder="All Categories"
          allowClear
          style={{ width: '100%' }}
          value={filters.category}
          onChange={handleCategoryChange}
          options={filterOptions.categories.map((cat) => ({
            value: cat.id,
            label: cat.name,
          }))}
        />
      </div>

      <div className="filter-section">
        <h4>Price Range</h4>
        <Slider
          range
          min={0}
          max={100000}
          step={1000}
          value={filters.priceRange}
          onChange={(value) => handlePriceRangeChange(value as [number, number])}
          tooltip={{
            formatter: (value) => `R${(value ?? 0).toLocaleString()}`,
          }}
        />
        <div className="price-range-labels">
          <span>R{filters.priceRange[0].toLocaleString()}</span>
          <span>R{filters.priceRange[1].toLocaleString()}</span>
        </div>
      </div>

      <div className="filter-section">
        <Tag.CheckableTag
          checked={filters.inStockOnly}
          onChange={(checked) =>
            setFilters((prev) => ({ ...prev, inStockOnly: checked }))
          }
        >
          In Stock Only
        </Tag.CheckableTag>
      </div>
    </div>
  );

  return (
    <div className="shop-page">
      {/* Search and Filter Bar */}
      <div className="shop-toolbar">
        <div className="shop-toolbar-left">
          <Search
            placeholder="Search products..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            className="shop-search"
            value={filters.searchText}
            onChange={(e) => setFilters((prev) => ({ ...prev, searchText: e.target.value }))}
            onSearch={handleSearch}
          />

          <Badge count={activeFilterCount} size="small">
            <Button
              icon={<FilterOutlined />}
              size="large"
              onClick={() => setFilterDrawerOpen(true)}
              className="filter-button"
            >
              Filters
            </Button>
          </Badge>

          {activeFilterCount > 0 && (
            <Button
              icon={<ClearOutlined />}
              size="large"
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          )}
        </div>

        <div className="shop-toolbar-right">
          <span className="result-count">
            {isLoading ? '...' : `${totalCount.toLocaleString()} products`}
          </span>
          <Select
            value={filters.sortBy}
            onChange={handleSortChange}
            style={{ width: 180 }}
            size="large"
            options={[
              { value: 'relevance', label: 'Relevance' },
              { value: 'name', label: 'Name (A-Z)' },
              { value: 'priceAsc', label: 'Price: Low to High' },
              { value: 'priceDesc', label: 'Price: High to Low' },
              { value: 'availability', label: 'Availability' },
            ]}
          />
        </div>
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="active-filters">
          {filters.brands.map((brandId) => {
            const brand = filterOptions.brands.find((b) => b.id === brandId);
            return brand ? (
              <Tag
                key={brandId}
                closable
                onClose={() => handleBrandToggle(brandId)}
              >
                {brand.name}
              </Tag>
            ) : null;
          })}
          {filters.category && (
            <Tag closable onClose={() => handleCategoryChange(undefined)}>
              {filterOptions.categories.find((c) => c.id === filters.category)?.name}
            </Tag>
          )}
          {(filters.priceRange[0] > 0 || filters.priceRange[1] < 100000) && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, priceRange: [0, 100000] }))
              }
            >
              R{filters.priceRange[0].toLocaleString()} - R{filters.priceRange[1].toLocaleString()}
            </Tag>
          )}
          {filters.inStockOnly && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, inStockOnly: false }))
              }
            >
              In Stock
            </Tag>
          )}
        </div>
      )}

      {/* Product Grid */}
      <div className={`product-grid ${isFetching && !isLoading ? 'loading' : ''}`}>
        {isLoading ? (
          renderSkeletonGrid()
        ) : products.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {products.map((product) => (
                <Col key={product.productIdentifier} {...GRID_COLUMNS}>
                  <ProductCard
                    product={product}
                    isAuthenticated={isAuthenticated}
                  />
                </Col>
              ))}
            </Row>
            {totalCount > PAGE_SIZE && (
              <div className="shop-pagination">
                <Pagination
                  current={currentPage}
                  total={totalCount}
                  pageSize={PAGE_SIZE}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} products`
                  }
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Filter Drawer (Mobile) */}
      <Drawer
        title="Filters"
        placement="left"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={320}
        footer={
          <Space>
            <Button onClick={handleClearFilters}>Clear All</Button>
            <Button type="primary" onClick={() => setFilterDrawerOpen(false)}>
              Apply Filters
            </Button>
          </Space>
        }
      >
        {FilterContent}
      </Drawer>
    </div>
  );
}

export default ShopPage;
